import { useEffect, useState } from "react";

/**
 * Returns `value` but only after it has stayed unchanged for `delayMs`.
 * Useful for type-to-search inputs where we want to fire the (potentially
 * expensive) `onSearch` once the user pauses typing.
 *
 * Example:
 *   const [query, setQuery] = useState("");
 *   const debounced = useDebouncedValue(query, 250);
 *   useEffect(() => { onSearch(debounced); }, [debounced]);
 */
export function useDebouncedValue<T>(value: T, delayMs: number = 250): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

export default useDebouncedValue;
